// By default, we always throw when an error is returned from an HTTP call.

import type { AccessTokenProvider } from "~application/auth/ports/access-token-provider.ts";
import type { ClientOptions } from "~infrastructure/.generated/hey/backoffice-backend";
import type { TAppConfig } from "~ui/@application/configs/config-schema.ts";

import { ELocale } from "~application/constants/locale.constant.ts";
import { createClient, createConfig } from "~infrastructure/.generated/hey/backoffice-backend/client";
import { backofficeAuthenticationResponseSchema } from "~infrastructure/api/backoffice/contract/authentication/responses/authentication-responses.ts";
import { sentryLogBackofficeSdkException } from "~ui/@core/composables/sentry/use-sentry.ts";
import { isStatusCodeSuccess } from "~utils/http-utils.ts";

export type BackofficeSdkClient = ReturnType<typeof makeSdkBackofficeClient>;

type BackofficeEnv = Pick<TAppConfig, "VITE_VOLTEXPLOIT_SERVICE_ID" | "VITE_BACKOFFICE_BASE_URL">;

interface BackofficeHeaders {
  serviceId: number; // deprecated, kept for backward compat
  "X-Voltalis-ServiceId": number; // deprecated, kept for backward compat
  "Voltalis-ServiceId": number;
}

export function makeSdkBackofficeClient(env: BackofficeEnv, tokenProvider: AccessTokenProvider) {
  return makeClient(env, tokenProvider);
}

// Track requests that have already been retried to prevent infinite loops
const retriedRequests = new WeakSet<Request>();

function makeClient(env: BackofficeEnv, tokenProvider: AccessTokenProvider) {
  const serviceId = env.VITE_VOLTEXPLOIT_SERVICE_ID;
  const headers = {
    serviceId,
    "X-Voltalis-ServiceId": serviceId,
    "Voltalis-ServiceId": serviceId,
  } satisfies BackofficeHeaders;

  const client = createClient(
    createConfig<ClientOptions>({
      baseUrl: env.VITE_BACKOFFICE_BASE_URL,
      headers,
      throwOnError: false,
      responseStyle: "fields",
    }),
  );

  client.interceptors.request.use((request) => {
    return localizeRequest(request).then((request) => authorizeRequest(request, tokenProvider));
  });
  client.interceptors.response.use(async (response, request) => {
    const finalResponse = await reauthenticateOnSessionExpired(response, request, env, tokenProvider);

    if (!isStatusCodeSuccess(response.status)) {
      await sentryLogBackofficeSdkException(finalResponse);
    }

    return finalResponse;
  });

  return { client, headers };
}

// Authentication

async function localizeRequest(request: Request): Promise<Request> {
  const mapping: Record<string, string> = {
    [ELocale.FRENCH]: "fr",
    [ELocale.ENGLISH]: "en",
    fr: "fr",
    en: "en",
  };

  const locale = document.documentElement.getAttribute("lang");
  const headerLocale = !locale ? "fr" : (mapping[locale] ?? "fr");

  request.headers.delete("Accept-Language");
  request.headers.set("Accept-Language", headerLocale);
  return request;
}

async function authorizeRequest(request: Request, tokenProvider: AccessTokenProvider): Promise<Request> {
  const maybeTokenDetails = await tokenProvider.getAccessToken();
  if (!maybeTokenDetails.success) {
    return request;
  }

  request.headers.append("Authorization", `Bearer ${maybeTokenDetails.value}`);

  return request;
}

async function reauthenticateOnSessionExpired(
  response: Response,
  request: Request,
  env: BackofficeEnv,
  tokenProvider: AccessTokenProvider,
): Promise<Response> {
  // If not a 401 error, return the response as-is
  if (response.status !== 401) {
    return response;
  }

  // If this request was already retried, don't retry again to prevent infinite loops
  if (retriedRequests.has(request)) {
    return response;
  }

  // Get the refresh token from local storage
  const maybeRefreshToken = await tokenProvider.getRefreshToken();
  if (!maybeRefreshToken.success) {
    return response;
  }

  try {
    // Call the refresh endpoint to get a new access token
    // TODO : Use generic sdk-generated call when it's available in the schema
    const refreshResponse = await fetch(`${env.VITE_BACKOFFICE_BASE_URL}/login/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        refresh_token: maybeRefreshToken.value,
      }),
    });

    if (!refreshResponse.ok) {
      console.error("Failed to refresh token:", refreshResponse.status);
      return response;
    }

    const newTokenDetails = await refreshResponse
      .json()
      .then((data) => backofficeAuthenticationResponseSchema.parse(data));

    await tokenProvider.saveAccessToken?.(newTokenDetails.access_token);
    await tokenProvider.saveRefreshToken?.(newTokenDetails.refresh_token);

    // Clone the original request and update the Authorization header with the new token
    const newHeaders = new Headers(request.headers);
    newHeaders.set("Authorization", `Bearer ${newTokenDetails.access_token}`);
    const newRequest = new Request(request, { headers: newHeaders });

    // Mark this request as retried to prevent infinite loops
    retriedRequests.add(newRequest);

    // Replay the original request with the new token
    const replayedResponse = await fetch(newRequest);

    return replayedResponse;
  } catch (e) {
    console.error("Error during token refresh:", e);
    return response;
  }
}
